import Page from 'src/components/Page';
import { Icon } from '@iconify/react';
import plusFill from '@iconify/icons-eva/plus-fill';
import React, { useEffect, useState, useRef} from "react";
import { Link as RouterLink, useLocation, useSearchParams } from 'react-router-dom';
import {
    Button,
    Chip,
    Container,
    Dialog,
    DialogContent,
    Drawer,
    Grid,
    IconButton,
    LinearProgress,
    Stack,
    Tooltip,
    Typography,
    useMediaQuery,
    createTheme,
    ThemeProvider,
    Skeleton,
    CircularProgress
} from '@mui/material';
import MUIDataTable, { ExpandButton } from "mui-datatables";
import * as constants from 'src/utils/constants';
import _, { set } from 'lodash';
import CalendarRangePicker from 'src/components/CalendarRangePicker';
import { debounce } from 'src/utils/debounceSearch';
import axios, { apiInstance } from 'src/utils/apiAuth';
import { showErrorToast, showSuccessToast } from 'src/utils/Toast/SnackbarProvider';
import { Add, CheckCircle, RemoveCircle, Cancel, Download, Upload, Close, Error, Warning, Update, Delete, Block } from '@mui/icons-material';
import sampleExcelFile from '../../../../assets/sample_excel_file.xlsx';
import BatchSchedule from '../journey/Timeline/BatchSchedule';
import AddCompany from './AddCompany';
import AddToDndDialog from './AddToDndDialog';
import { removeDnd } from 'src/utils/dndApi';
import { capitalizeWords, getDDMMYYYYFormat } from 'src/utils/commonUtils';

const theme = createTheme({
    components: {
        MUIDataTable: {
            styleOverrides: {
                root: {
                    backgroundColor: '#red',
                },
                paper: {
                    boxShadow: 'none',
                },
            },
        },
        MuiToolbar: {
            styleOverrides: {
                root: {
                    // backgroundColor: 'yellow',
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    "&.tss-1fz4yw6-MUIDataTableSelectCell-fixedLeft": {
                        position: "static !important",
                    },
                },
                head: {
                    // backgroundColor: 'purple',
                },
            },
        },
        MUIDataTableHeadCell: {
            styleOverrides: {
                root: {
                    fontWeight: '700',
                },
                data: {
                    fontWeight: '700',
                }
            }
        },
        MUIDataTableSelectCell: {
            styleOverrides: {
                headerCell: {
                    fontWeight: '500px'
                },
            },
        },
        MuiTableFooter: {
            styleOverrides: {
                root: {
                    '& .MuiToolbar-root': {
                        //   backgroundColor: 'purple',
                    },
                },
            },
        },
    },
    typography: {
        fontFamily: "Lexend"
    }
});

const components = {
    ExpandButton: function (props) {
        return <ExpandButton {...props} />;
    }
};

const StyledButton = {
    backgroundColor: "#00AB55",
    fontFamily: "Lexend,Public Sans,sans-serif",
    fontWeight: "700",
    borderRadius: "8px",
    boxShadow: "0 8px 16px 0 rgb(0 171 85 / 10%)"
}

export default function Companies() {
    const matches = useMediaQuery(theme.breakpoints.down('md'));

    const [isLoading, setIsLoading] = useState(true);

    const [currentPage, setCurrentPage] = useState(0);
    const [count, setCount] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedDateRange, setSelectedDateRange] = useState([]);
    const [filterParams, setFilterParams] = useState(null);
    const [sortOrder, setSortOrder] = useState();
  
    const location = useLocation();
    const params = new URLSearchParams(location.search);

    const debouncedSearch = debounce((query) => {
        setSearchQuery(query);
    }, 1000);

    // Function to append filters to the URL
    const applyFiltersToUrl = (filterParams) => {
        let url = "";

        // use filterParams to filter the data using the API
        for (const key in filterParams) {
            if (filterParams[key] && Array.isArray(filterParams[key]) && filterParams[key].length > 0) {
                url += `${key}=${filterParams[key].join(",")}&`;
            }
        }
        return url;
    };

    // Fetch companies
    const [companyData, setCompanyData] = useState([]);
    const getCompanies = async (page, pageSize = 50, query = '', selectedDateRange = [], filterParams = {}, sortOrder = null) => {
        let apiUrl = `company?`;
        if (query) apiUrl += `search=${query}&`;
        if (page + 1 && pageSize) apiUrl += `page=${page + 1}&page_size=${pageSize}&`;
        if (sortOrder && sortOrder?.name && sortOrder?.direction) apiUrl += `sortfield=${sortOrder.name}&sortdirection=${sortOrder.direction}&`;
        if (selectedDateRange.length > 0) apiUrl += `${selectedDateRange[0] ? `start_date=${selectedDateRange[0]}` : ''}${selectedDateRange[1] ? `&end_date=${selectedDateRange[1]}` : ''}${selectedDateRange[2] ? `&date_filter_type=${selectedDateRange[2]}&` : ''}`;

        // Append filters to the URL
        apiUrl += applyFiltersToUrl(filterParams);

        // remove the trailing '&' from the URL
        if (apiUrl.endsWith("&")) apiUrl = apiUrl.slice(0, -1);

        axios.get(apiUrl)
            .then((response) => {
                if (response?.data) {
                    setCompanyData(response?.data?.results?.data || []);
                    setCount(response?.data?.count || 0);
                }
            })
            .catch(error => {
                if (error) {
                    showErrorToast(error);
                }
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    // DND (Do-Not-Disturb) row action state + handlers
    const [dndDialogOpen, setDndDialogOpen] = useState(false);
    const [dndDialogCompany, setDndDialogCompany] = useState(null);

    const refetchCompanies = () => {
        getCompanies(currentPage, rowsPerPage, searchQuery, selectedDateRange, filterParams, sortOrder);
    };

    const handleOpenDndDialog = (company) => {
        setDndDialogCompany(company);
        setDndDialogOpen(true);
    };

    const handleRemoveDnd = async (companyId) => {
        if (!companyId) return;
        if (!window.confirm('Remove this company from the DND list?')) return;
        try {
            const response = await removeDnd(companyId);
            showSuccessToast(response?.data?.message || 'Company removed from DND list');
            refetchCompanies();
        } catch (error) {
            showErrorToast(error);
        }
    };

    const [userData, setUserData] = useState([]);
    const getUserData = () => {
        let apiUrl = `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}/api/v1/users-viewset?short_details=${true}&company_added=${true}`;
        axios
            .get(apiUrl, { headers: { Authorization: `Token ${localStorage.getItem("authToken")}` } })
            .then((response) => {
                let userData = response?.data?.data?.map((item) => ({
                    id: item.pk,
                    user: item.first_name + " " + item.last_name,
                }));
                setUserData(userData || []);
            })
            .catch((e) => console.log("something went wrong :(", e));
    };

    const [countryData, setCountryData] = useState([]);
    const getCountryData = () => {
        let apiUrl = `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}/api/v1/location?company_added=${true}`;
        axios
            .get(apiUrl, { headers: { Authorization: `Token ${localStorage.getItem("authToken")}` } })
            .then((response) => {
                let countryData = response?.data?.data
                    ?.filter((item) => item.is_active)
                    ?.map((item) => ({
                        id: item.id,
                        country: item.label,
                    }));
                    setCountryData(countryData || []);
            })
            .catch((e) => console.log("something went wrong :(", e));
    }

    const [confidenceLevelData, setConfidenceLevelData] = useState([]);
    const getConfidenceLevelData = () => {
        let apiUrl = `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}/api/v1/confidence-level?company_added=${true}`;
        axios
            .get(apiUrl, { headers: { Authorization: `Token ${localStorage.getItem("authToken")}` } })
            .then((response) => {
                let confidenceLevelData = response?.data?.data
                    ?.filter((item) => item.is_active)
                    ?.map((item) => ({
                        id: item.id,
                        confidence_level: item.label,
                    }));
                    setConfidenceLevelData(confidenceLevelData || []);
            })
            .catch((e) => console.log("something went wrong :(", e));
    }

    const [industryData, setIndustryData] = useState([]);
    const getIndustryData = () => {
        let apiUrl = `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}/api/v1/industry?company_added=${true}`;
        axios
            .get(apiUrl, { headers: { Authorization: `Token ${localStorage.getItem("authToken")}` } })
            .then((response) => {
                let industry = response?.data?.data
                    ?.filter((item) => item.is_active)
                    ?.map((item) => ({
                        id: item.id,
                        industry: item.label,
                    }));
                    setIndustryData(industry || []);
            })
            .catch((e) => console.log("something went wrong :(", e));
    }

    const [companyTypeData, setCompanyTypeData] = useState([]);
    const getCompanyTypeData = () => {
        let apiUrl = `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}/api/v1/company-type`;
        axios
            .get(apiUrl, { headers: { Authorization: `Token ${localStorage.getItem("authToken")}` } })
            .then((response) => {
                let companyTypeData = response?.data?.data
                    ?.filter((item) => item.is_active)
                    ?.map((item) => ({
                        id: item.id,
                        companyType: item.label,
                    }));
                    setCompanyTypeData(companyTypeData || []);
            })
            .catch((e) => console.log("something went wrong :(", e));
    }

    const [lastStageTaskData, setLastStageTaskData] = useState([]);
    const getLastStageTaskData = () => {
        let apiUrl = `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}/api/v1/company/get_last_stage_task_names`;
        axios.get(apiUrl, { headers: { Authorization: `Token ${localStorage.getItem("authToken")}` } })
            .then((response) => {
                let lastStageTasks = response?.data?.data
                    ?.map((item) => ({
                        id:item.last_stage_task_id,
                        last_stage_task:item.last_stage_task_name
                    }));
                setLastStageTaskData(lastStageTasks || [])
            })
            .catch((e) => console.log("something went wrong :(", e));
    }

    const [stageData, setStageData] = useState([]);
    const getStageData = () => {
        let apiUrl = `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}/api/v1/company/get_stage_names`;
        axios.get(apiUrl, { headers: { Authorization: `Token ${localStorage.getItem("authToken")}` } })
            .then((response) => {
                let stages = response?.data?.data
                    ?.map((item) => ({
                        id: item.stage_id,
                        stage: item.stage_name
                    }));
                setStageData(stages || [])
            })
            .catch((e) => console.log("something went wrong :(", e));
    }

    const [selectedCompanyIds, setSelectedCompanyIds] = useState([]);
    const [selectedRows, setSelectedRows] = useState([]);

    const STORAGE_KEY = 'companyTableColumns';

    // MUI Data Table Options
    const options = {
        selectableRows: "multiple",
        selectToolbarPlacement: 'none',
        filter: true,
        customToolbar: () => (
            <Tooltip title={selectedCompanyIds?.length === 0 ? "Select companies to delete" : ""}>
                <span>
                    <IconButton
                        onClick={handleBulkDelete}
                        disabled={!selectedCompanyIds || selectedCompanyIds.length === 0}
                        style={{
                            color: selectedCompanyIds?.length > 0 ? '#d32f2f' : undefined,
                        }}
                    >
                        <Delete />
                    </IconButton>
                </span>
            </Tooltip>
        ),
        setFilterChipProps: (colIdx, colName, data) => {
            const column = columns.find(col => col.name === colName);
            const label = column ? column.label : colName; // Fallback to colName if no label is found

            return {
                label: `${label}: ${data}`,
            };
        },
        search: true,
        searchOpen: false,
        searchProps: {
            style: { width: '250px' }
        },
        print: false,
        viewColumns: true,
        filterType: "multiselect",
        responsive: "standard",
        rowsSelected: selectedRows.map(row => companyData.findIndex(item => item.id === row.id)),
        draggableColumns: { enabled: true },
        serverSide: true,
        download: false,
        count: count,
        rowsPerPage: rowsPerPage,
        rowsPerPageOptions: [10, 20, 30, 50, 100, 200, 300, 400, 500],
        page: currentPage,

        onTableChange: (action, tableState) => {
            switch (action) {
                case 'changePage':
                    setCurrentPage(tableState.page);
                    break;
                case 'changeRowsPerPage':
                    setRowsPerPage(tableState.rowsPerPage);
                    break;
                case 'search':
                    debouncedSearch(tableState.searchText);
                    break;
                case 'sort':
                    setSortOrder(tableState.sortOrder);
                    break;
                case 'rowSelectionChange': {
                    const selectedIndexes = tableState.selectedRows.data.map(row => row.index);
                    const selectedRows = selectedIndexes.map(index => companyData[index]);
                    setSelectedRows(selectedRows);
                    const selectedIds = selectedRows.map(row => row.id);
                    setSelectedCompanyIds(selectedIds);
                    break;
                }
                case 'viewColumnsChange': {
                    // Save column visibility to localStorage
                    const visibleColumns = tableState.columns
                        .filter(col => col.display === 'true')
                        .map(col => col.name);
                    localStorage.setItem(STORAGE_KEY, JSON.stringify(visibleColumns));
                    break;
                }
                default:
                    break;
            }
        },
        onFilterChange: (column, filterList, type) => {
            const index = columns.findIndex((item) => item.name === column);
            const filterValue = filterList[index];

            setCurrentPage(0);

            const mapFilterValueToIds = (data, property="name") => {
                return filterValue.length > 0
                    ? filterValue.map((item) => data.find((entry) => entry[property] === item).id)
                    : [];
            };

            let newFilterParams = { ...filterParams };

            switch (column) {
                case 'country':
                    newFilterParams.country = mapFilterValueToIds(countryData, "country");
                    break;
                case 'confidence_level':
                    newFilterParams.confidence_level = mapFilterValueToIds(confidenceLevelData, "confidence_level");
                    break;
                case 'industry':
                    newFilterParams.industry = mapFilterValueToIds(industryData, "industry");
                    break;
                case 'company_type':
                    newFilterParams.company_type = mapFilterValueToIds(companyTypeData, "companyType");
                    break;
                case 'created_by':
                    newFilterParams.created_by = mapFilterValueToIds(userData, "user")
                    break;
                case 'initial_email_status':
                case 'opened_initial_email_status':
                case 'follow_up_email':
                    newFilterParams[column] = filterValue;
                    break;
                case 'has_scheduled_meeting':
                    newFilterParams[column] = filterValue;
                    break;
                case 'last_stage_task':
                    newFilterParams[column] = mapFilterValueToIds(lastStageTaskData, "last_stage_task");
                    break;
                case 'stage':
                    newFilterParams[column] = mapFilterValueToIds(stageData, "stage");
                    break;
                case null:
                    newFilterParams = {};
                    break;
                default:
                    break;
            }

            // Remove the property if it has no value
            if (newFilterParams[column] && newFilterParams[column].length === 0) {
                delete newFilterParams[column];
            }

            setTableColumns((prevColumns) =>
                prevColumns.map((col) => {
                    if (col.name === column) {
                        let newFilterList = [];
                        switch (col.name) {
                            case 'country':
                                newFilterList = newFilterParams.country
                                    ? newFilterParams.country.map((id) => countryData.find((cs) => cs.id === id)?.country).filter(Boolean)
                                    : [];
                                break;
                            case 'confidence_level':
                                newFilterList = newFilterParams.confidence_level
                                    ? newFilterParams.confidence_level.map((id) => confidenceLevelData.find((cl) => cl.id === id)?.confidence_level).filter(Boolean)
                                    : [];
                                break;
                            case 'industry':
                                newFilterList = newFilterParams.industry
                                    ? newFilterParams.industry.map((id) => industryData.find((cl) => cl.id === id)?.industry).filter(Boolean)
                                    : [];
                                break;
                            case 'company_type':
                                newFilterList = newFilterParams.company_type
                                    ? newFilterParams.company_type.map((id) => companyTypeData.find((ct) => ct.id === id)?.companyType).filter(Boolean)
                                    : [];
                                break;
                            case 'created_by':
                                newFilterList = newFilterParams.created_by
                                    ? newFilterParams.created_by.map((id) => userData.find((u) => u.id === id)?.user).filter(Boolean)
                                    : [];
                                break;
                            case 'initial_email_status':
                            case 'opened_initial_email_status':
                            case 'follow_up_email':
                            case 'has_scheduled_meeting':
                                newFilterList = newFilterParams[col.name] || [];
                                break;
                            case 'last_stage_task':
                                newFilterList = newFilterParams.last_stage_task ? newFilterParams.last_stage_task.map((id) => lastStageTaskData.find((lst) => lst.id === id)?.last_stage_task).filter(Boolean) : [];
                                break;
                            case 'stage':
                                newFilterList = newFilterParams.stage ? newFilterParams.stage.map((id) => stageData.find((s) => s.id === id)?.stage).filter(Boolean) : [];
                                break;
                            default:
                                newFilterList = [];
                        }
                        return {
                            ...col,
                            options: {
                                ...col.options,
                                filterList: newFilterList,
                            },
                        };
                    }
                    return col;
                })
            );
            
            setFilterParams(newFilterParams);
        },
    };

    // MUI Data Table Columns
    const columns = [
        {
            name: "id",
            label: "ID",
            options: {
                filter: false
            },
        },
        {
            name: "company_name",
            label: "Name",
            options: {
                filter: false,
                setCellProps: (value, rowIndex) => {
                    const company = companyData[rowIndex];
                    const isReminderDue = company?.is_reminder_due;
                    return {
                        style: {
                            backgroundColor: isReminderDue ? '#fee2e2' : (company?.is_duplicate ? '#ffcccb' : 'inherit')
                        }
                    };
                },
                customBodyRender: (value, tableMeta, updateValue) => {
                    var cid = tableMeta.rowData[0];
                    const rowIndex = tableMeta.rowIndex;
                    const company = companyData[rowIndex];
                    const isReminderDue = company?.is_reminder_due;
                    const activeReminder = company?.active_reminder;
                    
                    return (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <RouterLink
                                to={`${cid}`}
                                state={{ fromPage: "companies" }}
                                style={{
                                    textDecoration: 'none',
                                    color: '#00AB55',
                                    fontWeight: 'bold'
                                }}>
                                {value}
                            </RouterLink>
                            {isReminderDue && (
                                <Tooltip title={activeReminder?.note ? `Due Reminder: ${activeReminder.note}` : 'Reminder is due!'}>
                                    <Chip
                                        label="⏰ DUE"
                                        size="small"
                                        sx={{
                                            bgcolor: '#fee2e2',
                                            color: '#b91c1c',
                                            fontWeight: 700,
                                            fontSize: '0.65rem',
                                            height: '20px',
                                            border: '1px solid #ef4444',
                                        }}
                                    />
                                </Tooltip>
                            )}
                            {activeReminder && !isReminderDue && (
                                <Tooltip title={`Reminder: ${activeReminder.reminder_date ? new Date(activeReminder.reminder_date).toLocaleDateString() : ''} - ${activeReminder.note || 'No note'}`}>
                                    <Chip
                                        label="🔔"
                                        size="small"
                                        sx={{
                                            bgcolor: '#dbeafe',
                                            color: '#1d4ed8',
                                            fontWeight: 700,
                                            fontSize: '0.65rem',
                                            height: '20px',
                                            border: '1px solid #3b82f6',
                                        }}
                                    />
                                </Tooltip>
                            )}
                        </div>
                    )
                }
            }
        },
        {
            name: "website",
            label: "Website",
            options: {
                filter: false,
                customBodyRender: (value, tableMeta, updateValue) => {
                    var cid = tableMeta.rowData[0];
                    return (
                        <a href={value} target='_blank' rel='noreferrer'>
                            {value}
                        </a>
                    )
                }
            }
        },
        {
            name: "country",
            label: "Country",
            options: {
                filter: true,
                filterList: filterParams?.country?.map((s) => countryData?.find(cs => cs.id === s)?.country),
                filterOptions: {
                    fullWidth: true,
                    names: countryData.map((item) => item.country),
                },
            }
        },
        {
            name: "confidence_level",
            label: "Confidence Level",
            options: {
                filter: true,
                filterOptions: {
                    fullWidth: true,
                    names: confidenceLevelData.map((item) => item.confidence_level),
                },
            }
        },
        {
            name: "industry",
            label: "Industry",
            options: {
                filter: true,
                filterOptions: {
                    fullWidth: true,
                    names: industryData.map((item) => item.industry),
                },
            }
        },
        {
            name: "company_type",
            label: "Company Type",
            options: {
                filter: false,
                filterOptions: {
                    fullWidth: true,
                    names: companyTypeData.map((item) => item.companyType),
                },
            }
        },
        {
            name: "revenue",
            label: "Revenue",
            options: {
                filter: false,
            }
        },
        {
            name: "fte",
            label: "FTE",
            options: {
                filter: false,
            }
        },
        {
            name: "foundation_year",
            label: "Foundation Year",
            options: {
                filter: false,
                customBodyRender: (value) => {
                    return (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            {value}
                        </div>
                    )
                }
            }
        },
        {
            name: "last_update",
            label: "Last Update",
            options: {
                filter: false,
                setCellProps: () => ({
                    style: {
                        width: "250px",
                        minWidth: "250px",
                        height: "75px",
                        overflow: "hidden",
                        display: "table-cell",         // allow vertical alignment
                        verticalAlign: "middle",       // vertical centering
                        padding: "8px",
                        boxSizing: "border-box"
                    }
                }),
                customBodyRender: (value) => {
                    return (
                        <div
                            style={{
                                display: "-webkit-box",
                                WebkitBoxOrient: "vertical",
                                WebkitLineClamp: 3,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "normal",
                                lineHeight: "1.2em",
                                maxHeight: "3.6em",
                                width: "100%",
                            }}
                        >
                            <Tooltip title={value} placement="top" arrow>
                                <div
                                    style={{
                                        width: "250px",
                                        maxWidth: "250px",
                                    }}
                                >
                                    {value}
                                </div>
                            </Tooltip>
                        </div>
                    );
                }
            }
        },
        {
            name: 'last_stage_task',
            label: "Last Stage",
            options: {
                filter: true,
                filterList: filterParams?.last_stage_task?.map((s) => lastStageTaskData?.find(cs => cs.id === s)?.last_stage_task),
                filterOptions: {
                    fullWidth: true,
                    names: lastStageTaskData.map((item)=>item.last_stage_task) || [],
                }
            }
        },
        {
            name: 'stage',
            label: "Stage",
            options: {
                filter: true,
                display: false,
                filterList: filterParams?.stage?.map((s) => stageData?.find(cs => cs.id === s)?.stage),
                filterOptions: {
                    fullWidth: true,
                    names: stageData.map((item) => item.stage) || [],
                },
                customBodyRender: (value) => {
                    return value || "-";
                }
            }
        },
        {
            name: "initial_email_status",
            label: "Initial Outreach",
            options: {
                filter: false,
                filterOptions: {
                    fullWidth: true,
                    names: ['Not Sent', 'Sent', 'Failed', 'Opened', 'Not Opened'],
                },
                sort: false,
                customBodyRender: (value) => {
                    return (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            {value ? (value?.toLowerCase().includes("sent") ?
                                <CheckCircle fontSize='medium' color='success' />
                                : <Error fontSize='medium' color='error' />)
                                : <RemoveCircle fontSize='medium' color='action' />}
                        </div>
                    )
                },
            }
        },
        {
            name: "opened_initial_email_status",
            label: "Opened Initial Outreach",
            options: {
                filter: false,
                sort: false,
                customBodyRender: (value) => {
                    return (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            {value ? (value?.toLowerCase().includes("not opened") ?
                                <Cancel fontSize='medium' color='error' />
                                : <CheckCircle fontSize='medium' color='success' />)
                                : <RemoveCircle fontSize='medium' color='action' />}
                        </div>
                    )
                }
            }
        },
        {
            name: "has_scheduled_meeting",
            label: "Scheduled Meeting",
            options: {
                filter: true,
                filterOptions: {
                    fullWidth: true,
                    names: ['Yes', 'No'],
                },
                sort: false,
                customBodyRender: (value) => {
                    return (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            {value === true ?
                                <CheckCircle fontSize='medium' color='success' />
                                : <RemoveCircle fontSize='medium' color='action' />
                            }
                        </div>
                    )
                }
            }
        },
        {
            name: "created_by",
            label: "Created By",
            options: {
                filter: true,
                display: false,
                filterList: filterParams?.created_by?.map((s) => userData?.find(cs => cs.id === s)?.user),
                filterOptions: {
                    fullWidth: true,
                    names: userData.map((item) => item.user) || [],
                },
                customBodyRender: (value) => {
                    return (
                        value ? value?.first_name + " " + value?.last_name : "PORTAL"
                    )
                }
            }
        },
        {
            name: "modified",
            label: "Last Modified",
            options: {
                filter: false,
                customBodyRender: (value) => {
                    return (
                        <p>{getDDMMYYYYFormat(value)}</p>
                    )
                }
            }
        },
        {
            name: "dnd",
            label: "DND",
            options: {
                filter: false,
                sort: false,
                empty: true,
                // `is_dnd` / `dnd_details` live on the raw CompanyListSerializer row, not on the
                // mapped MUIDataTable data object, so look the full row up by id (rowData[0]) —
                // the same companyData-lookup idiom used by the company_name cell above.
                customBodyRender: (value, tableMeta) => {
                    const cid = tableMeta.rowData[0];
                    const company = companyData.find((c) => c?.id === cid);
                    const isDnd = company?.is_dnd === true;
                    const details = company?.dnd_details;

                    if (isDnd) {
                        const tip = [details?.reason_display, details?.note].filter(Boolean).join(' — ');
                        return (
                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                <Tooltip title={tip ? `${tip} (click to remove)` : 'On DND list (click to remove)'}>
                                    <Chip
                                        label="DND"
                                        size="small"
                                        onClick={() => handleRemoveDnd(cid)}
                                        sx={{
                                            bgcolor: '#fee2e2',
                                            color: '#b91c1c',
                                            fontWeight: 700,
                                            fontSize: '0.7rem',
                                            height: '22px',
                                            border: '1px solid #ef4444',
                                            cursor: 'pointer',
                                        }}
                                    />
                                </Tooltip>
                            </div>
                        );
                    }

                    return (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <Tooltip title="Add to DND">
                                <IconButton
                                    size="small"
                                    onClick={() => handleOpenDndDialog({ id: cid, company_name: company?.company_name })}
                                >
                                    <Block fontSize="small" color="action" />
                                </IconButton>
                            </Tooltip>
                        </div>
                    );
                }
            }
        },
    ];

    const [tableColumns, setTableColumns] = useState(columns);

    // Load saved column settings on component mount
    useEffect(() => {
        const savedColumnsStr = localStorage.getItem(STORAGE_KEY);
        if (savedColumnsStr && countryData && companyTypeData && userData && lastStageTaskData && stageData && industryData) {
            const visibleColumns = JSON.parse(savedColumnsStr);

            // Apply visibility settings to columns while keeping filterOptions intact
            const updatedColumns = columns.map(col => ({
                ...col,
                options: {
                    ...col.options,  // Retain existing options like filterOptions
                    display: visibleColumns.includes(col.name),  // Update only display
                }
            }));
            setTableColumns(updatedColumns);
        } else {
            // Initialize with default columns
            const defaultVisibleColumns = columns
                .filter(col => col?.options?.display !== false)
                .map(col => col?.name);
            localStorage.setItem(STORAGE_KEY, JSON.stringify(defaultVisibleColumns));
        }
    }, [countryData, userData, companyTypeData, lastStageTaskData, stageData, industryData]);
    
    useEffect(() => {
        if (params.size === 0) {
            getCompanies(currentPage, rowsPerPage, searchQuery, selectedDateRange, filterParams, sortOrder);     
        }
    }, [currentPage, rowsPerPage, searchQuery, selectedDateRange, filterParams, sortOrder, location.search]);

    useEffect(() => {
        getUserData();
        getCountryData();
        getCompanyTypeData();
        getLastStageTaskData();
        getStageData();
        getIndustryData()
    }, []);

    const handleDownloadSampleExcel = () => {
        const link = document.createElement('a');
        link.href = sampleExcelFile;
        link.download = 'Sample_Excel_File.xlsx';
        link.click();
    };

    const [isImportExcelDialogOpen, setIsImportExcelDialogOpen] = useState(false);
    const handleImportExcelClick = () => {
        setIsImportExcelDialogOpen(true);
    }

    const [excelImportSummary, setExcelImportSummary] = useState(null);
    const [errorExcelFileLink, setErrorExcelFileLink] = useState(null);
    const handleExcelFileUpload = () => {
        setExcelImportSummary(null);
        setErrorExcelFileLink(null);
        document.getElementById('excelFileInput').value = '';
        document.getElementById('excelFileInput').click();
    }

    const [importStatus, setImportStatus] = useState('Pending');
    const handleUploadExcel = (event) => {
        const selectedFile = event.target.files[0];
        const MAX_SIZE_MB = 50;
        const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;
        if (!selectedFile) return; // No file selected

        setImportStatus('Uploading');

        if (selectedFile.size > MAX_SIZE_BYTES) {
            showErrorToast(null, "Please select a file smaller than 500 MB.");
            return;
        }

        const acceptedTypes = ['.xls', '.xlsx'];
        const isValidType = acceptedTypes.some(type => selectedFile.name.endsWith(type));

        if (!isValidType) {
            showErrorToast(null, "Only Excel file are allowed.");
            return;
        }

        callImportCompanyDataFromExcel(selectedFile);
    };

    const [isExcelImportProcessing, setIsExcelImportProcessing] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isExcelImportCompleted, setIsExcelImportCompleted] = useState(false);

    const callImportCompanyDataFromExcel = (selectedFile) => {
        setIsExcelImportProcessing(true);
        setImportStatus('Processing');

        const formData = new FormData();
        formData.append('file', selectedFile);

        apiInstance({
            method: "post",
            url: 'company/import_company_data_from_excel/',
            data: formData,
            headers: {
                Authorization: `Token ${localStorage.getItem('authToken')}`,
            },
            onUploadProgress: (progressEvent) => {
                const { loaded, total } = progressEvent;
                const percent = Math.floor((loaded * 100) / total);
                setUploadProgress(percent);
            },
        })
            .then(function (response) {
                setImportStatus('Validating');
                if (response) {
                    if (response?.data?.errors?.length > 0) {
                        setImportStatus('Completed With Errors');
                    } else {
                        setImportStatus('Completed');
                    }
                    setExcelImportSummary(response?.data?.summary);
                    if (response?.data?.error_log_file) setErrorExcelFileLink(response?.data?.error_log_file);
                }
            })
            .catch(function (error) {
                showErrorToast(error);
                setImportStatus('Failed');
            })
            .finally(() => {
                setIsExcelImportProcessing(false);
                setIsExcelImportCompleted(true);
                setImportStatus(null);
            });
    };

    const handleDownloadErrorLog = () => {
        if (errorExcelFileLink) {
            apiInstance({
                url: `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}${errorExcelFileLink}`,
                method: 'GET',
                responseType: 'blob',
            }).then((response) => {
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                const date = new Date().toLocaleDateString().split('/').join('_');
                link.download = `Failed_Companies_Log_${date}.xlsx`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            });
        }
    };

    const [loading, setLoading] = useState(false);
    const fetchEmailUsage = async () => {
        try {
            setLoading(true);
            const response = await axios.get(
                `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}/api/v1/campaigns/sendgrid-usage/`, 
                { headers: { "Authorization": `Token ${localStorage.getItem('authToken')}` } }
            );
    
            if (response?.data) {
                return response.data;
            }
            return null;
        } catch (error) {
            let error_msg_key = error?.response?.data && typeof error?.response?.data === "object"
                ? Object.keys(error?.response?.data)?.[0]
                : null;
            let err_msg = error?.response?.data?.message || error?.response?.data?.[error_msg_key];
            showErrorToast(error?.customMessage || err_msg || "Something went wrong. Please try after sometime.");
            return null;
        } finally {
            setLoading(false);
        }
    };

    const [emailType, setEmailType] = useState(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isSendGridInfoOpen, setIsSendGridInfoOpen] = useState(false);
    const [sendGridUsageData, setSendGridUsageData] = useState(null);
    const handleSendGridUsage = async (type) => {
        setEmailType(type);
        const data = await fetchEmailUsage();
        setSendGridUsageData(data);
        setIsSendGridInfoOpen(true);
    }

    const [disabledInitialOutreachMessage, setDisabledInitialOutreachMessage] = useState(null);
    const [isAddCompanyDrawerOpen, setIsAddCompanyDrawerOpen] = useState(false);

    const handleBulkDelete = async () => {
        if (!selectedCompanyIds || selectedCompanyIds.length === 0) return;
        
        if (!window.confirm(`Are you sure you want to delete ${selectedCompanyIds.length} companies?`)) return;
        
        try {
            const response = await apiInstance({
                method: 'post',
                url: 'company/bulk-delete/',
                data: { ids: selectedCompanyIds },
                headers: { 
                        Authorization: `Token ${localStorage.getItem('authToken')}`,
                    }
            });
            
            if (response?.data?.success) {
                showErrorToast(null, response.data.message || 'Companies deleted successfully');
                setSelectedCompanyIds([]);
                setSelectedRows([]);
                getCompanies(currentPage, rowsPerPage, searchQuery, selectedDateRange, filterParams, sortOrder);
            }
        } catch (error) {
            showErrorToast(error);
        }
    };

    useEffect(() => {
        if (selectedCompanyIds?.length < 1) {
            setDisabledInitialOutreachMessage("Not more than 1 companies are selected");
        } else {
            setDisabledInitialOutreachMessage(null);
        }
    }, [selectedCompanyIds, companyData]);

    useEffect(() => {
        if (params.size > 0 && location.search) {
            const queryKeys = ["country", "last_stage_task", "created_by", "stage"];
            const paramObj = queryKeys.reduce((acc, key) => {
                const value = params.get(key);
                if (value !== null) {
                    acc[key] = [key === "country" || key === "stage" || key === "last_stage_task" ? parseInt(value, 10) : value];
                }
                return acc;
            }, {});
            setFilterParams(paramObj);
            const startDate = params.get("start_date");
            const endDate = params.get("end_date");
            const filterType = params.get("date_filter_type");
            const dateRange = [
                startDate || "",
                endDate || "",
                filterType || ""
            ];
            getCompanies(currentPage, rowsPerPage, searchQuery, dateRange, paramObj, sortOrder);
        }
    }, [])

    const isInitialRender = useRef(true);

    useEffect(() => {
        const startDate = params.get("start_date");
        const endDate = params.get("end_date");
        const filterType = params.get("date_filter_type");

        const dateRangeToUse = selectedDateRange.length > 0
            ? selectedDateRange
            : [startDate || "", endDate || "", filterType || ""];

        const filtersReady = filterParams !== null;

        if (isInitialRender.current && filtersReady) {
            isInitialRender.current = false;
            return;
        }
        if (isInitialRender.current) {
            return;
        }
        getCompanies(currentPage, rowsPerPage, searchQuery, dateRangeToUse, filterParams, sortOrder);
    }, [currentPage, rowsPerPage, searchQuery, selectedDateRange, filterParams, sortOrder]); 

    return (
        <Page title="Companies | CRM">
            <Container maxWidth="xl">
                {isLoading ? (
                    <div style={{ padding: '16px', marginTop: "20px" }}>
                        {/* Skeleton for the Table Header */}
                        <div style={{ display: 'flex', marginBottom: '20px' }}>
                            {columns.map((column, colIndex) => (
                                <Skeleton
                                    key={colIndex}
                                    variant="text"
                                    width={'30%'}
                                    style={{ marginRight: '16px' }}
                                    height={50}
                                />
                            ))}
                        </div>
                        {/* Skeletons for the Table Rows */}
                        {Array.from(new Array(rowsPerPage)).map((_, rowIndex) => (
                            <div key={rowIndex} style={{ display: 'flex', marginBottom: '10px' }}>
                                {columns.map((column, colIndex) => (
                                    <Skeleton
                                        key={colIndex}
                                        variant="text"
                                        width={'30%'}
                                        style={{ marginRight: '16px' }}
                                        height={50}
                                    />
                                ))}
                            </div>
                        ))}
                    </div>
                ) : (
                    <ThemeProvider theme={theme}>
                        <MUIDataTable
                            title="Companies"
                            data={companyData.filter(data => data?.is_active !== false).map(data => ({
                                id: data?.id,
                                company_name: data?.company_name,
                                website: data?.website,
                                country: data?.country?.label ? data?.country?.label : null,
                                confidence_level: data?.confidence_level?.label ? data?.confidence_level?.label : null,
                                industry: data?.industry?.label ? data?.industry?.label : null,
                                company_type: data?.company_type?.map((obj) => obj?.label).join(", "),
                                revenue: (data?.currency?.label && data?.revenue) ? (data?.currency?.label + " " + data?.revenue) : null,
                                fte: data?.fte,
                                foundation_year: data?.foundation_year,
                                last_update: data?.last_update,
                                last_stage_task: data?.last_stage_task?.stage?.label && data?.last_stage_task?.task?.label
                                    ? `${data.last_stage_task.stage.label} - ${data.last_stage_task.task.label}`
                                    : '',
                                stage: data?.last_stage_task?.stage?.label || null,
                                initial_email_status: data?.status ? (['sent', 'delivered', 'opened', 'clicked'].includes(data?.status) ? 'Sent' : data?.status) : null,
                                opened_initial_email_status: data?.status ? (['sent', 'delivered'].includes(data?.status) ? 'Not Opened' : (['opened', 'clicked'].includes(data?.status) ? 'Opened' : null)) : null,
                                has_scheduled_meeting: data?.has_scheduled_meeting,                                
                                created_by: data?.created_by,
                                modified: data?.modified,
                                is_duplicate: data?.is_duplicate,
                            }))}
                            columns={tableColumns}
                            options={options}
                            components={components}
                        />
                    </ThemeProvider>
                )}
            </Container>
            {isSendGridInfoOpen && (
                <Dialog maxWidth="xs" fullWidth open={isSendGridInfoOpen} onClose={() => setIsSendGridInfoOpen(false)}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-end" m={2}>
                        <div></div>
                        <h2 align="center" style={{ fontSize: '22px', fontWeight: '800' }}>SendGrid Usage of this month</h2>
                        <IconButton onClick={() => { setIsSendGridInfoOpen(false); }}>
                            <Close fontSize={"medium"} />
                        </IconButton>
                    </Stack>
                    <Stack spacing={2} mx={4}>
                        <Typography>Total Contacts: <b>{sendGridUsageData?.contacts_count}</b></Typography>
                        <Typography>ResumeX Email Usage: <b>{sendGridUsageData?.resumex_sent}</b></Typography>
                        <Typography>CRM Email Usage: <b>{sendGridUsageData?.growthx_sent}</b></Typography>
                    </Stack>
                    <Tooltip title={(sendGridUsageData?.contacts_count > 10000 || (sendGridUsageData?.resumex_sent + sendGridUsageData?.growthx_sent) > 30000) ? 'SendGrid limit exceeded' : ''}>
                        <span style={{margin: '5% auto'}} >
                            <Button 
                                onClick={() => setIsDialogOpen(true)} 
                                variant='contained' 
                                color='success'
                                disabled={sendGridUsageData?.contacts_count > 10000 || (sendGridUsageData?.resumex_sent + sendGridUsageData?.growthx_sent) > 30000}
                            >
                                Start Bulk Outreach 
                            </Button>
                        </span>
                    </Tooltip>
                    <Stack bgcolor='#FFDEAD' mt={2} p={2}>
                        <Typography mx={2} variant='subtitle2'>For more details visit <a href="https://app.sendgrid.com/account/products" target='_blank' rel='noreferrer' color='orange'>SendGrid Account Details</a></Typography>
                    </Stack>
                </Dialog>
            )}
            {isDialogOpen && emailType ?
                <Dialog maxWidth="md" fullWidth open={isDialogOpen} onClose={() => setIsDialogOpen(false)}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <div></div>
                        <Typography variant='h5' textAlign='center' mt={2}>Batch Scheduling for {capitalizeWords(emailType)}</Typography>
                        <IconButton onClick={() => { setIsDialogOpen(false); }}>
                            <Close fontSize={matches ? "small" : "medium"} />
                        </IconButton>
                    </Stack>
                    <BatchSchedule companyIDs={selectedCompanyIds} type={emailType} />
                </Dialog>
                : null}
            {isImportExcelDialogOpen ?
                <Dialog maxWidth='sm' fullWidth open={isImportExcelDialogOpen} onClose={() => { setIsImportExcelDialogOpen(false); setExcelImportSummary(null); setErrorExcelFileLink(null); window.location.reload(); }}>
                    <DialogContent sx={{ px: 3 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography align='center' variant={matches ? "body" : "h5"}>Import Excel</Typography>
                            <IconButton onClick={() => { setIsImportExcelDialogOpen(false); setExcelImportSummary(null); setErrorExcelFileLink(null); window.location.reload(); }}>
                                <Close fontSize={matches ? "small" : "medium"} />
                            </IconButton>
                        </Stack>
                        <DialogContent>
                            <Stack direction='column' spacing={2}>
                                <Stack spacing={1} border='1px solid lightgrey' borderRadius='8px' padding={2}>
                                    <Stack direction='row' spacing={1} alignItems='center'>
                                        <Typography><Error fontSize='medium' color='error' /></Typography>
                                        <Typography variant='subtitle1'>Important!</Typography>
                                    </Stack>
                                    <Stack ml='30px'>
                                        <Typography>Please ensure you upload the file in the exact format as the sample file. Otherwise, company data will not be added.</Typography>
                                    </Stack>
                                </Stack>
                                <Stack>
                                    {isExcelImportProcessing && (
                                        <Stack spacing={1} alignItems='center'>
                                            {importStatus}
                                            <LinearProgress variant="determinate" value={uploadProgress} sx={{ width: '100%', mt: 2 }} />
                                        </Stack>
                                    )}
                                    {isExcelImportCompleted && excelImportSummary ? (
                                        <Stack spacing={2}>
                                            <Stack spacing={1} border='1px solid lightgrey' borderRadius='8px' padding={2}>
                                                <Typography variant="subtitle1">Icon Legend</Typography>
                                                <Stack direction="column" spacing={2}>
                                                    <Stack direction='row' alignItems='center' spacing={1}>
                                                        <CheckCircle fontSize="small" style={{ color: 'white', background: 'green', borderRadius: '50%' }} />
                                                        <Typography variant="caption">Created: Successfully added.</Typography>
                                                    </Stack>
                                                    <Stack direction='row' alignItems='center' spacing={1}>
                                                        <Update fontSize="medium" style={{ color: 'orange', marginLeft: -3 }} />
                                                        <Typography variant="caption">Updated: Information has been updated successfully.</Typography>
                                                    </Stack>
                                                    <Stack direction='row' alignItems='center' spacing={1}>
                                                        <Error fontSize="small" style={{ color: 'white', background: 'red', borderRadius: '50%' }} />
                                                        <Typography variant="caption">Failed: An error occurred during processing.</Typography>
                                                    </Stack>
                                                </Stack>
                                            </Stack>
                                            <Typography variant="h6">Import Summary</Typography>
                                            <Stack spacing={2} width='max-content'>
                                                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent='space-between'>
                                                    <Typography>Company</Typography>
                                                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                                                        <Stack alignItems='center' direction='row' spacing={1}>
                                                            <Tooltip title="Created">
                                                                <CheckCircle fontSize="small" style={{ color: 'white', background: 'green', borderRadius: '50%' }} />
                                                            </Tooltip>
                                                            <Typography variant="h6">{excelImportSummary?.companies_created}</Typography>
                                                        </Stack>
                                                        <Stack alignItems='center' direction='row' spacing={1}>
                                                            <Tooltip title="Updated">
                                                                <Update fontSize="medium" style={{ color: 'orange', background: '' }} />
                                                            </Tooltip>
                                                            <Typography variant="h6">{excelImportSummary?.companies_updated}</Typography>
                                                        </Stack>
                                                        <Stack alignItems='center' direction='row' spacing={1}>
                                                            <Tooltip title="Failed">
                                                                <Error fontSize="small" style={{ color: 'white', background: 'red', borderRadius: '50%' }} />
                                                            </Tooltip>
                                                            <Typography variant="h6">{excelImportSummary?.companies_failed}</Typography>
                                                        </Stack>
                                                    </Stack>
                                                </Stack>
                                                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent='space-between'>
                                                    <Typography>Members</Typography>
                                                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                                                        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                                                            <Stack alignItems='center' direction='row' spacing={1}>
                                                                <Tooltip title="Created">
                                                                    <CheckCircle fontSize="small" style={{ color: 'white', background: 'green', borderRadius: '50%' }} />
                                                                </Tooltip>
                                                                <Typography variant="h6">{excelImportSummary?.members_created}</Typography>
                                                            </Stack>
                                                            <Stack alignItems='center' direction='row' spacing={1}>
                                                                <Tooltip title="Updated">
                                                                    <Update fontSize="medium" style={{ color: 'orange', background: '' }} />
                                                                </Tooltip>
                                                                <Typography variant="h6">{excelImportSummary?.members_updated}</Typography>
                                                            </Stack>
                                                            <Stack alignItems='center' direction='row' spacing={1}>
                                                                <Tooltip title="Failed">
                                                                    <Error fontSize="small" style={{ color: 'white', background: 'red', borderRadius: '50%' }} />
                                                                </Tooltip>
                                                                <Typography variant="h6">{excelImportSummary?.members_failed}</Typography>
                                                            </Stack>
                                                        </Stack>
                                                    </Stack>
                                                </Stack>
                                                <Typography variant="body1">Total Companies: <b>{excelImportSummary?.total_companies}</b></Typography>
                                            </Stack>
                                            {excelImportSummary?.failed_companies?.length > 0 && (
                                                <Stack spacing={1}>
                                                    <Stack direction='row' spacing={1} alignItems='center'>
                                                        <Typography><Warning fontSize='medium' color='warning' /></Typography>
                                                        <Typography variant='subtitle1'>Failed Companies</Typography>
                                                    </Stack>
                                                    <Grid item>
                                                        {excelImportSummary.failed_companies.map((company) => (
                                                            <Chip size='small' label={company} style={{ margin: 2 }} key={company} />
                                                        ))}
                                                    </Grid>
                                                </Stack>
                                            )}
                                        </Stack>
                                    ) : null}
                                </Stack>
                                {errorExcelFileLink && (
                                    excelImportSummary?.errors?.length > 0 && (
                                        <Stack spacing={1} width='max-content'>
                                            <Button 
                                                variant='text' 
                                                color='error'
                                                startIcon={<Download />}
                                                onClick={() => handleDownloadErrorLog()}
                                                style={{ background: 'rgba(255, 0, 0, 0.1)'}}
                                            >
                                                Download Error Log
                                            </Button>
                                        </Stack>
                                    )
                                )}
                                <Stack spacing={2} direction={{ xs: "column", sm: "row" }} justifyContent='center'>
                                    <Button
                                        variant="contained"
                                        startIcon={<Upload />}
                                        style={StyledButton}
                                        onClick={() => handleExcelFileUpload()}
                                    >Choose Excel File</Button>
                                    <input
                                        id="excelFileInput"
                                        type="file"
                                        accept=".xls, .xlsx"
                                        style={{ display: 'none' }}
                                        onChange={(event) => handleUploadExcel(event)}
                                    />
                                    <Button
                                        startIcon={<Download />}
                                        style={{ color: 'green', border: '1px solid green', borderRadius: '8px' }}
                                        onClick={() => handleDownloadSampleExcel()}
                                    >
                                        Download Sample Excel File
                                    </Button>
                                </Stack>
                            </Stack>
                        </DialogContent>
                    </DialogContent>
                </Dialog>
                : null}
            <Drawer 
                anchor="right" 
                open={isAddCompanyDrawerOpen} 
                onClose={() => setIsAddCompanyDrawerOpen(false)}
                PaperProps={{
                    sx: { width: { xs: '100%', sm: '600px', md: '800px' } }
                }}
            >
                <AddCompany setOpenDrawer={setIsAddCompanyDrawerOpen}/>
            </Drawer>
            <AddToDndDialog
                open={dndDialogOpen}
                onClose={() => { setDndDialogOpen(false); setDndDialogCompany(null); }}
                company={dndDialogCompany}
                onDone={refetchCompanies}
            />
        </Page>
    )
}